import React, { useEffect, useState, useCallback } from "react";
import { Card, Row, Col, Table, Select, DatePicker, Input, Space, Button, Collapse, message, Dropdown, Menu, Checkbox } from "antd";
import axios from "axios";
import moment from "moment";
import ENVIRONMENT from "../../../../environments";
import { LoadingOutlined, SearchOutlined, FilterOutlined, DownloadOutlined } from "@ant-design/icons";
import { exportToCSV, generateExportFilename } from "../../Utilities";
import { withRouter } from "react-router-dom";

const DATE_FORMAT = "YYYY-MM-DD";
const ALL_TIME_START = "2020-01-01";

const ModelCostDetails = ({ companies = [], modelStatus, history, location }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [modelDetails, setModelDetails] = useState([]);
  const [startDate, setStartDate] = useState(
    moment().subtract(1, "months").startOf("month").format(DATE_FORMAT)
  );
  const [endDate, setEndDate] = useState(
    moment().subtract(1, "months").endOf("month").format(DATE_FORMAT)
  );
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [allCustomers, setAllCustomers] = useState([]); // Store all customers for filtering
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [categories, setCategories] = useState([]);
  const [customStartDate, setCustomStartDate] = useState(
    moment().subtract(1, "months").startOf("month")
  );
  const [customEndDate, setCustomEndDate] = useState(moment());
  const [selectedValue, setSelectedValue] = useState("1");
  const [filtersVisible, setFiltersVisible] = useState(false);
  const [isInitialStateSet, setIsInitialStateSet] = useState(false);
  // Column visibility state
  const [columnVisibility, setColumnVisibility] = useState({
    invoiceable_creation_hours: false,
    non_invoiceable_creation_hours: false,
    invoiceable_fixes_hours: false,
    non_invoiceable_fixes_hours: false,
    num_of_fixes: false,
    customer_rejection_count: false,
    admin_rejection_count: false
  });
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  // Table search state
  const [searchText, setSearchText] = useState("");
  const [searchedColumn, setSearchedColumn] = useState("");
  const searchInput = React.useRef(null);

  const handleSearch = (selectedKeys, confirm, dataIndex) => {
    confirm();
    setSearchText(selectedKeys[0] || "");
    setSearchedColumn(dataIndex);
  };

  const handleReset = (clearFilters) => {
    clearFilters();
    setSearchText("");
  };

  const getColumnSearchProps = (dataIndex, title) => ({
    filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters, close }) => (
      <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
        <Input
          ref={searchInput}
          placeholder={`Search ${title}`}
          value={selectedKeys[0]}
          onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
          onPressEnter={() => handleSearch(selectedKeys, confirm, dataIndex)}
          style={{ marginBottom: 8, display: 'block' }}
        />
        <Space>
          <Button
            type="primary"
            onClick={() => handleSearch(selectedKeys, confirm, dataIndex)}
            icon={<SearchOutlined />}
            size="small"
            style={{ width: 90 }}
          >
            Search
          </Button>
          <Button
            onClick={() => {
              if (clearFilters) handleReset(clearFilters);
              confirm({ closeDropdown: true });
            }}
            size="small"
            style={{ width: 90 }}
          >
            Reset
          </Button>
        </Space>
      </div>
    ),
    filterIcon: (filtered) => (
      <SearchOutlined style={{ color: filtered ? '#1677ff' : undefined }} />
    ),
    onFilter: (value, record) => {
      const target = record[dataIndex];
      if (target === undefined || target === null) return false;
      return target.toString().toLowerCase().includes(value.toLowerCase());
    },
  });

  const getModelLink = (id, model_type) => {
    if (model_type === "Material") {
      return "/admin_materials/" + id;
    } else if (
      model_type === "Space Model Variation" ||
      model_type === "Space Base Model"
    ) {
      return "/admin_rooms/" + id;
    } else {
      return "/admin_products/" + id;
    }
  };

  const columns = [
    {
      title: "Model ID",
      dataIndex: "id",
      key: "id",
      sorter: (a, b) => (parseFloat(a.id) || 0) - (parseFloat(b.id) || 0),
      render: (text, record) => (
        <a className="onhover-underline" href={getModelLink(text, record.model_type)}>
          {text}
        </a>
      ),
    },
    {
      title: "Model Name",
      dataIndex: "name",
      key: "name",
      ...getColumnSearchProps('name', 'Model Name'),
    },
    {
      title: "Username",
      dataIndex: "customer_username",
      key: "customer_username",
      ...getColumnSearchProps('customer_username', 'Username'),
    },
    {
      title: "Company",
      dataIndex: "company",
      key: "company",
      ...getColumnSearchProps('company', 'Company'),
    },
    {
      title: "Category",
      dataIndex: "category",
      key: "category",
      ...getColumnSearchProps('category', 'Category'),
    },
    {
      title: "Invoiceable Creation Hours",
      dataIndex: "invoiceable_creation_hours",
      key: "invoiceable_creation_hours",
      sorter: (a, b) => (parseFloat(a.invoiceable_creation_hours) || 0) - (parseFloat(b.invoiceable_creation_hours) || 0),
      render: (text, record) => (record.invoiceable_creation_hours || 0),
    },
    {
      title: "Non-Invoiceable Creation Hours",
      dataIndex: "non_invoiceable_creation_hours",
      key: "non_invoiceable_creation_hours",
      sorter: (a, b) => (parseFloat(a.noninvoiceable_creation_hours) || 0) - (parseFloat(b.noninvoiceable_creation_hours) || 0),
      render: (text, record) => (record.noninvoiceable_creation_hours || 0),
    },
    {
      title: "Invoiceable Fixes Hours",
      dataIndex: "invoiceable_fixes_hours",
      key: "invoiceable_fixes_hours",
      sorter: (a, b) => (parseFloat(a.invoiceable_fixes_hours) || 0) - (parseFloat(b.invoiceable_fixes_hours) || 0),
      render: (text, record) => (record.invoiceable_fixes_hours || 0),
    },
    {
      title: "Non-Invoiceable Fixes Hours",
      dataIndex: "non_invoiceable_fixes_hours",
      key: "non_invoiceable_fixes_hours",
      sorter: (a, b) => (parseFloat(a.noninvoiceable_fixes_hours) || 0) - (parseFloat(b.noninvoiceable_fixes_hours) || 0),
      render: (text, record) => (record.noninvoiceable_fixes_hours || 0),
    },
    {
      title: "Number of Fixes",
      dataIndex: "num_of_fixes",
      key: "num_of_fix",
      sorter: (a, b) => (parseFloat(a.num_fixes) || 0) - (parseFloat(b.num_fixes) || 0),
      render: (text, record) => (record.num_fixes || 0),
    },
    {
      title: "Customer Rejection Count",
      dataIndex: "customer_rejection_count",
      key: "customer_rejection_count",
      sorter: (a, b) => (parseFloat(a.customer_rejection_count) || 0) - (parseFloat(b.customer_rejection_count) || 0),
      render: (text, record) => (record.customer_rejection_count || 0),
    },
    {
      title: "Admin Rejection Count",
      dataIndex: "admin_rejection_count",
      key: "admin_rejection_count",
      sorter: (a, b) => (parseFloat(a.admin_rejection_count) || 0) - (parseFloat(b.admin_rejection_count) || 0),
      render: (text, record) => (record.admin_rejection_count || 0),
    },
    {
      title: "Artist Cost",
      dataIndex: "artist_modeling_cost",
      key: "artist_modeling_cost",
      sorter: (a, b) => (parseFloat(a.artist_modeling_cost) || 0) - (parseFloat(b.artist_modeling_cost) || 0),
      render: (text) => "$" + text,
    },
    {
      title: "Automation Cost",
      dataIndex: "automation_cost",
      key: "automation_cost",
      sorter: (a, b) => (parseFloat(a.automation_cost) || 0) - (parseFloat(b.automation_cost) || 0),
      render: (text) => "$" + text,
    },
    {
      title: "Total Cost",
      dataIndex: "total_cost",
      key: "total_cost",
      sorter: (a, b) => (parseFloat(a.total_cost) || 0) - (parseFloat(b.total_cost) || 0),
      render: (text) => "$" + text,
    },
  ];

  // Initialize column visibility to true for all columns on first mount
  useEffect(() => {
    if (!isInitialStateSet) return;
    // Only initialize once
    setColumnVisibility((prev) => {
      if (Object.keys(prev).length > 0) return prev;
      const initial = {};
      columns.forEach((col) => {
        if (col.dataIndex) initial[col.dataIndex] = true;
      });
      return initial;
    });
  }, [isInitialStateSet]);

  const handleColumnVisibilityChange = (dataIndex, checked) => {
    setColumnVisibility((prev) => ({ ...prev, [dataIndex]: checked }));
  };

  const getVisibleColumns = () => {
    // Keep columns visible if true or if visibility not set yet (default true)
    return columns.filter((col) => {
      if (!col.dataIndex) return true; // keep action/id-like columns without dataIndex
      const visible = columnVisibility[col.dataIndex];
      return visible === undefined ? true : visible;
    });
  };

  const getShowColumnsMenu = (
    <Menu>
      {columns.map((col) => {
        if (!col.dataIndex) return null;
        const titleText = typeof col.title === 'string' ? col.title : (col.title?.props?.children?.[0] || '');
        return (
          <Menu.Item key={col.dataIndex}>
            <span onClick={(e) => e.stopPropagation()}>
              <Checkbox
                checked={columnVisibility[col.dataIndex] !== false}
                onChange={(e) => handleColumnVisibilityChange(col.dataIndex, e.target.checked)}
              >
                {titleText || col.dataIndex}
              </Checkbox>
            </span>
          </Menu.Item>
        );
      })}
    </Menu>
  );

  const fetchAllModelDetails = React.useCallback(() => {
    // Prevent multiple simultaneous API calls
    if (isLoading) {
      console.log('API call already in progress, skipping...');
      return;
    }
    const payload = {
      action: "get_model_details",
      start_date: startDate,
      end_date: endDate,
      is_approved: modelStatus == 'approved',
    };

    if (selectedCustomer && selectedCustomer !== "all") {
      payload.customer = selectedCustomer;
    }
    
    // Convert company value to proper format for API
    const companyId = getCompanyIdForAPI(selectedCompany);
    if (companyId != null) {
      payload.company_id = companyId;
    }
    
    if (selectedCategories && Array.isArray(selectedCategories) && selectedCategories.length > 0) {
      payload.category = selectedCategories;
    }

    console.log('Making API call with payload:', payload);
    setIsLoading(true);
    axios
    .post(ENVIRONMENT.GET_MODELING_COST_REPORT, payload)
    .then((response) => {
      const responseData = response.data || [];
      // Flatten metadata fields to top level of each record
      const flattenedData = responseData.map(record => {
        const flattened = { ...record };
        
        // Move metadata fields to top level
        if (record.metadata) {
          flattened.invoiceable_creation_hours = record.metadata.invoiceable_creation_hours || 0;
          flattened.noninvoiceable_creation_hours = record.metadata.noninvoiceable_creation_hours || 0;
          flattened.invoiceable_fixes_hours = record.metadata.invoiceable_fixes_hours || 0;
          flattened.noninvoiceable_fixes_hours = record.metadata.noninvoiceable_fixes_hours || 0;
          flattened.num_fixes = record.metadata.num_fixes || 0;
          flattened.customer_rejection_count = record.metadata.customer_rejection_count || 0;
          flattened.admin_rejection_count = record.metadata.admin_rejection_count || 0;
        }
        
        return flattened;
      });
      
      setModelDetails(flattenedData);
    })
    .finally(() => setIsLoading(false));
  }, [startDate, endDate, selectedCustomer, selectedCompany, selectedCategories, isLoading, modelStatus]);

  // Parse URL query parameters for ModelCostDetails
  const parseURLParams = () => {
    const searchParams = new URLSearchParams(location.search);
    return {
      startDate: searchParams.get('mcd_sd') || moment().subtract(1, "months").startOf("month").format(DATE_FORMAT),
      endDate: searchParams.get('mcd_ed') || moment().subtract(1, "months").endOf("month").format(DATE_FORMAT),
      selectedCustomer: searchParams.get('mcd_c') || "all",
      selectedCompany: searchParams.get('mcd_cp') || "all",
      selectedCategories: (searchParams.get('mcd_cat') || "")
        .split(',')
        .map((v) => v.trim())
        .filter((v) => v.length > 0),
      selectedValue: searchParams.get('mcd_dr') || "1",
      currentPage: searchParams.get('mcd_page') ? parseInt(searchParams.get('mcd_page'), 10) : 1,
      pageSize: searchParams.get('mcd_pageSize') ? parseInt(searchParams.get('mcd_pageSize'), 10) : 10
    };
  };

  // Update URL with current filter state
  const updateURL = (filters) => {
    const searchParams = new URLSearchParams(location.search);
    
    // Update ModelCostDetails params
    if (filters.startDate) searchParams.set('mcd_sd', filters.startDate);
    if (filters.endDate) searchParams.set('mcd_ed', filters.endDate);
    
    // Handle customer filter
    if (filters.selectedCustomer !== undefined) {
      if (filters.selectedCustomer && filters.selectedCustomer !== "all") {
        searchParams.set('mcd_c', filters.selectedCustomer);
      } else {
        searchParams.delete('mcd_c');
      }
    }
    
    // Handle company filter
    if (filters.selectedCompany !== undefined) {
      if (filters.selectedCompany && filters.selectedCompany !== "all") {
        searchParams.set('mcd_cp', filters.selectedCompany);
      } else {
        searchParams.delete('mcd_cp');
      }
    }
    
    // Handle categories filter (comma-separated list in URL)
    if (filters.selectedCategories !== undefined) {
      const list = Array.isArray(filters.selectedCategories) ? filters.selectedCategories : [];
      if (list.length > 0) {
        searchParams.set('mcd_cat', list.join(','));
      } else {
        searchParams.delete('mcd_cat');
      }
    }
    
    if (filters.selectedValue) searchParams.set('mcd_dr', filters.selectedValue);
    
    // Handle pagination
    if (filters.currentPage !== undefined) {
      if (filters.currentPage && filters.currentPage > 1) {
        searchParams.set('mcd_page', filters.currentPage.toString());
      } else {
        searchParams.delete('mcd_page');
      }
    }
    
    if (filters.pageSize !== undefined) {
      if (filters.pageSize && filters.pageSize !== 10) {
        searchParams.set('mcd_pageSize', filters.pageSize.toString());
      } else {
        searchParams.delete('mcd_pageSize');
      }
    }

    const newURL = `${location.pathname}?${searchParams.toString()}`;
    history.replace(newURL);
  };

  // Handle pagination change
  const handleTableChange = (pagination, filters, sorter) => {
    setCurrentPage(pagination.current);
    setPageSize(pagination.pageSize);
    
    // Update URL with new pagination state
    updateURL({
      currentPage: pagination.current,
      pageSize: pagination.pageSize
    });
  };

  // Initialize state from URL params
  useEffect(() => {
    const urlParams = parseURLParams();
    setStartDate(urlParams.startDate);
    setEndDate(urlParams.endDate);
    setSelectedCustomer(urlParams.selectedCustomer);
    setSelectedCompany(urlParams.selectedCompany);
    setSelectedCategories(urlParams.selectedCategories);
    setSelectedValue(urlParams.selectedValue);
    setCurrentPage(urlParams.currentPage);
    setPageSize(urlParams.pageSize);
    
    // Set custom dates if needed
    if (urlParams.selectedValue === 'custom') {
      setCustomStartDate(moment(urlParams.startDate));
      setCustomEndDate(moment(urlParams.endDate));
    }
    
    // Mark that initial state has been set
    setIsInitialStateSet(true);
  }, []);

  // Trigger API call when filters change or on initial load
  useEffect(() => {
    console.log('useEffect triggered with:', {
      startDate,
      endDate,
      selectedCustomer,
      selectedCompany,
      selectedCategories,
      isInitialStateSet
    });
    
    // Only make API calls after initial state has been set
    if (isInitialStateSet && startDate && endDate) {
      console.log('Calling fetchAllModelDetails...');
      fetchAllModelDetails();
    }
  }, [isInitialStateSet, startDate, endDate, selectedCustomer, selectedCompany, selectedCategories, modelStatus]);



  // Handle company value conversion for API calls
  const getCompanyIdForAPI = (companyValue) => {
    console.log('getCompanyIdForAPI called with:', companyValue);
    
    if (!companyValue || companyValue === "all") return null;
    
    // If it's already an array of IDs, return it
    if (Array.isArray(companyValue)) return companyValue;
    
    // If it's a company name, find the corresponding ID
    const company = companies.find(c => c.value === companyValue);
    const result = company ? company.value : companyValue;
    console.log('getCompanyIdForAPI returning:', result);
    return result;
  };

  // Customers Dropdown
  const getUsersList = useCallback(() => {
    const payload = {};
    axios.post(ENVIRONMENT.CUSTOMER_GET_BATCH, payload).then((response) => {
      let users = [{ label: "All Customers", value: "all" }];
      if (response.data) {
        response.data.map((request) => {
          users.push({ 
            label: request.customer_username, 
            value: request.customer_username,
            company_id: request.company_id // Store company_id for filtering
          });
        });
        setAllCustomers(users); // Store all customers
        setCustomers(users); // Initially show all customers
      }
    });
  }, []);

  // Filter customers based on selected company
  const filterCustomersByCompany = useCallback((companyId) => {
    if (!companyId || companyId === "all") {
      setCustomers(allCustomers); // Show all customers
    } else {
      const filteredCustomers = allCustomers.filter(customer => 
        customer.value === "all" || customer.company_id === companyId
      );
      setCustomers(filteredCustomers);
    }
  }, [allCustomers]);

  // Categories Dropdown functions
  const getCategoryList = useCallback(() => {
    const payload = {};
    
    // Add company_id and customer if selected
    if (selectedCompany && selectedCompany !== "all") {
      if (Array.isArray(selectedCompany)) {
        payload.company_id = selectedCompany[0];
      } else {
        payload.company_id = selectedCompany;
      }
    }
    if (selectedCustomer && selectedCustomer !== "all") {
      payload.customer_username = selectedCustomer;
    }
    
    axios.post(ENVIRONMENT.GET_ALL_CATEGORIES, payload).then((response) => {
      let categoriesData = [];
      if (response.data) {
        response.data.map((request) => {
          categoriesData.push({ label: request.category, value: request.value });
        });
        setCategories(categoriesData);
      }
    }).catch(() => {
      setCategories([]);
    });
  }, [selectedCompany, selectedCustomer]);

  const handleCustomerChange = (value) => {
    if (value === undefined || value === "all") {
      setSelectedCustomer("all");
    } else {
      setSelectedCustomer(value);
    }
    
    // Reset category selection when customer changes
    setSelectedCategories([]);
    
    // Reset pagination to page 1 when filters change
    setCurrentPage(1);
    
    // Update URL
    updateURL({ 
      selectedCustomer: value === undefined ? "all" : value,
      currentPage: 1
    });
  };

  // Company Dropdown (uses companies from parent)
  const handleCompanyChange = (value) => {
    if (value === undefined || value === "all") {
      setSelectedCompany("all");
    } else {
      setSelectedCompany(value); // value should be company_id from parent options
    }
    
    // Reset customer and category selection when company changes
    setSelectedCustomer("all");
    setSelectedCategories([]);
    
    // Reset pagination to page 1 when filters change
    setCurrentPage(1);
    
    // Filter customers for the selected company
    filterCustomersByCompany(value);
    
    // Update URL
    updateURL({ 
      selectedCompany: value === undefined ? "all" : value,
      currentPage: 1
    });
  };

  // Category Dropdown (supports multiple selection)
  const handleCategoryChange = (values) => {
    const rawList = Array.isArray(values) ? values : [];
    // If 'all' is selected, clear all selections and do not persist any categories
    if (rawList.includes('all')) {
      setSelectedCategories([]);
      setCurrentPage(1);
      updateURL({ 
        selectedCategories: [],
        currentPage: 1
      });
      return;
    }

    const list = rawList.filter((v) => v);
    setSelectedCategories(list);

    // Reset pagination to page 1 when filters change
    setCurrentPage(1);

    // Update URL
    updateURL({ 
      selectedCategories: list,
      currentPage: 1
    });
  };

  useEffect(() => {
    getUsersList();
  }, []);

  // Filter customers when company changes (for initial load from URL params)
  useEffect(() => {
    if (isInitialStateSet && selectedCompany && selectedCompany !== "all") {
      filterCustomersByCompany(selectedCompany);
    }
  }, [isInitialStateSet, selectedCompany, filterCustomersByCompany]);

  // Fetch categories when company or customer changes
  useEffect(() => {
    if (isInitialStateSet) {
      getCategoryList();
    }
  }, [isInitialStateSet, selectedCompany, selectedCustomer]);

  // Fetch default categories when no filters are applied (initial load)
  useEffect(() => {
    if (isInitialStateSet && (!selectedCompany || selectedCompany === "all") && (!selectedCustomer || selectedCustomer === "all")) {
      // Only fetch default categories if no specific filters are applied
      const payload = {};
      axios.post(ENVIRONMENT.GET_ALL_CATEGORIES, payload).then((response) => {
      let categoriesData = [];
        if (response.data) {
          response.data.map((request) => {
            categoriesData.push({ label: request.category, value: request.value });
          });
          setCategories(categoriesData);
        }
      }).catch(() => {
        setCategories([]);
      });
    }
  }, [isInitialStateSet, selectedCompany, selectedCustomer]);

  // Date Dropdown
  const dateOptions = [
    {
      label: "Current Month",
      value: "0",
    },
    {
      label: "Last Month",
      value: "1",
    },
    {
      label: moment().subtract(2, "months").format("MMMM YYYY"),
      value: "2",
    },
    {
      label: moment().subtract(3, "months").format("MMMM YYYY"),
      value: "3",
    },
    {
      label: "All Time",
      value: "all_time",
    },
    {
      label: "Custom",
      value: "custom",
    },
  ];

  const handleDateChange = (value) => {
    if (value === "all_time") {
      setSelectedValue(value);
      const newStartDate = ALL_TIME_START;
      const newEndDate = moment().format(DATE_FORMAT);
      setStartDate(newStartDate);
      setEndDate(newEndDate);
      
      // Reset pagination to page 1 when filters change
      setCurrentPage(1);
      
      // Update URL
      updateURL({
        startDate: newStartDate,
        endDate: newEndDate,
        selectedValue: value,
        currentPage: 1
      });
    } else if (value === "custom") {
      // handled in onSelect
    } else {
      setSelectedValue(value);
      const numberOfMonths = parseInt(value, 10);
      const newStartDate = moment().subtract(numberOfMonths, "months").startOf("month").format(DATE_FORMAT);
      const newEndDate = moment().subtract(numberOfMonths, "months").endOf("month").format(DATE_FORMAT);
      setStartDate(newStartDate);
      setEndDate(newEndDate);
      
      // Reset pagination to page 1 when filters change
      setCurrentPage(1);
      
      // Update URL
      updateURL({
        startDate: newStartDate,
        endDate: newEndDate,
        selectedValue: value,
        currentPage: 1
      });
    }
  };

  const handleDateSelect = (value) => {
    if (value === "custom") {
      setSelectedValue(value);
    }
  };

  const handleCustomDateChange = (dates) => {
    if (dates && dates.length === 2) {
      const start = dates[0];
      const end = dates[1];
      if (start && end && !start.isAfter(end)) {
        setCustomStartDate(start);
        setCustomEndDate(end);
        const newStartDate = start.format(DATE_FORMAT);
        const newEndDate = end.format(DATE_FORMAT);
        setStartDate(newStartDate);
        setEndDate(newEndDate);
        
        // Reset pagination to page 1 when filters change
        setCurrentPage(1);
        
        // Update URL
        updateURL({
          startDate: newStartDate,
          endDate: newEndDate,
          currentPage: 1
        });
      }
    }
  };

  const handleDisabledDates = (date) => {
    if (
      date.format(DATE_FORMAT) < ALL_TIME_START ||
      date.format(DATE_FORMAT) > moment().format(DATE_FORMAT)
    ) {
      return true;
    }
    return false;
  };

  const titleColSpan = selectedValue === 'custom' ? 4 : 8;

  const handleExport = () => {
    if (!modelDetails || modelDetails.length === 0) {
      message.warning('No data to export');
      return;
    }

    const filename = generateExportFilename('Model-cost-details', startDate, endDate);
    
    // Define export columns (can be different from table columns)
    const exportColumns = [
      { title: 'Model ID', dataIndex: 'id' },
      { title: 'Model Name', dataIndex: 'name' },
      { title: 'Username', dataIndex: 'customer_username' },
      { title: 'Company', dataIndex: 'company' },
      { title: 'Category', dataIndex: 'category' },
      { title: 'Invoiceable Creation Hours', dataIndex: 'invoiceable_creation_hours' },
      { title: 'Non-Invoiceable Creation Hours', dataIndex: 'noninvoiceable_creation_hours' },
      { title: 'Invoiceable Fixes Hours', dataIndex: 'invoiceable_fixes_hours' },
      { title: 'Non-Invoiceable Fixes Hours', dataIndex: 'noninvoiceable_fixes_hours' },
      { title: 'Number of Fixes', dataIndex: 'num_fixes' },
      { title: 'Customer Rejection Count', dataIndex: 'customer_rejection_count' },
      { title: 'Admin Rejection Count', dataIndex: 'admin_rejection_count' },
      { title: 'Artist Cost', dataIndex: 'artist_modeling_cost' },
      { title: 'Automation Cost', dataIndex: 'automation_cost' },
      { title: 'Total Cost', dataIndex: 'total_cost' },
    ];

    // Custom value formatter for currency fields
    const valueFormatter = (value, row, col) => {
      if (col.dataIndex === 'artist_modeling_cost' || 
          col.dataIndex === 'automation_cost' || 
          col.dataIndex === 'total_cost') {
        return value ? `$${value}` : '$0';
      }
      return value;
    };

    exportToCSV(filename, exportColumns, modelDetails, {
      valueFormatter: valueFormatter
    });
  };

  // Table summary row
  const getTableSummary = useCallback(() => {
    if (!modelDetails?.length) return null;
    
    const totals = modelDetails.reduce((acc, record) => ({
      artistCost: acc.artistCost + (parseFloat(record.artist_modeling_cost) || 0),
      automationCost: acc.automationCost + (parseFloat(record.automation_cost) || 0),
      totalCost: acc.totalCost + (parseFloat(record.total_cost) || 0),
      count: acc.count + 1
    }), { artistCost: 0, automationCost: 0, totalCost: 0, count: 0 });

    const averages = {
      artistCost: totals.count > 0 ? totals.artistCost / totals.count : 0,
      automationCost: totals.count > 0 ? totals.automationCost / totals.count : 0,
      totalCost: totals.count > 0 ? totals.totalCost / totals.count : 0
    };

    const visibleColumns = getVisibleColumns();
    const artistIdx = visibleColumns.findIndex((c) => c.dataIndex === 'artist_modeling_cost');
    const automationIdx = visibleColumns.findIndex((c) => c.dataIndex === 'automation_cost');
    const totalIdx = visibleColumns.findIndex((c) => c.dataIndex === 'total_cost');

    // Build dynamic summary cells aligned with currently visible columns
    const cells = [];
    const firstColSpan = artistIdx > 0 ? artistIdx : 1; // merge up to artist column if present

    for (let i = 0; i < visibleColumns.length; i++) {
      if (i === 0) {
        cells.push(
          <Table.Summary.Cell index={i} colSpan={firstColSpan} key={`summary-col-${i}`}>
            <div style={{ display: 'flex', justifyContent: 'center' }}>Averages</div>
          </Table.Summary.Cell>
        );
        // If we merged multiple cells, the next (firstColSpan - 1) cells must have colSpan=0
        for (let hiddenIdx = 1; hiddenIdx < firstColSpan; hiddenIdx++) {
          cells.push(
            <Table.Summary.Cell index={i + hiddenIdx} colSpan={0} key={`summary-col-${i + hiddenIdx}`} />
          );
        }
        i += (firstColSpan - 1);
        continue;
      }

      if (i === artistIdx) {
        cells.push(
          <Table.Summary.Cell index={i} key={`summary-col-${i}`}>
            <span>${averages.artistCost.toFixed(2)}</span>
          </Table.Summary.Cell>
        );
        continue;
      }
      if (i === automationIdx) {
        cells.push(
          <Table.Summary.Cell index={i} key={`summary-col-${i}`}>
            <span>${averages.automationCost.toFixed(2)}</span>
          </Table.Summary.Cell>
        );
        continue;
      }
      if (i === totalIdx) {
        cells.push(
          <Table.Summary.Cell index={i} key={`summary-col-${i}`}>
            <span>${averages.totalCost.toFixed(2)}</span>
          </Table.Summary.Cell>
        );
        continue;
      }

      // Default empty cell for other columns
      cells.push(
        <Table.Summary.Cell index={i} key={`summary-col-${i}`} />
      );
    }

    return (
      <Table.Summary.Row style={{ backgroundColor: 'transparent', fontWeight: 'bold' }}>
        {cells}
      </Table.Summary.Row>
    );
  }, [modelDetails, columnVisibility]);

  return (
    <Card
      className="no-hover section-container"
      size="small"
      title={
        <div>
          <Row className="mt-10 justify-in-start" gutter={12}>
            <Col span={12}>
              <span className="sub-heading">Model Cost Details</span>
            </Col>
            <Col span={12} className="justify-in-end">
              <Button
                icon={<DownloadOutlined />}
                onClick={handleExport}
                style={{ marginRight: 8 }}
                disabled={!modelDetails || modelDetails.length === 0}
              >
                Export CSV
              </Button>
              <Dropdown overlay={getShowColumnsMenu} trigger={["click"]}>
                <Button style={{ marginRight: 8 }}>
                  Show Columns
                </Button>
              </Dropdown>
              <Button
                type="primary"
                icon={<FilterOutlined />}
                onClick={() => setFiltersVisible(!filtersVisible)}
              >
                {filtersVisible ? 'Hide Filters' : 'Show Filters'}
              </Button>
            </Col>
          </Row>
          
          {filtersVisible && (
            <Row gutter={12} style={{ marginTop: 16 }}>
              <Col span={6}>
                <Select
                  showSearch
                  placeholder="Select Company"
                  style={{ width: "100%" }}
                  options={companies || []}
                  onChange={handleCompanyChange}
                  optionFilterProp="label"
                  value={selectedCompany}
                  allowClear
                  className="analytics-filter-centered-search-select"
                />
              </Col>
              <Col span={6}>
                <Select
                  showSearch
                  placeholder="Select Customer"
                  style={{ width: "100%" }}
                  options={customers}
                  onChange={handleCustomerChange}
                  optionFilterProp="label"
                  value={selectedCustomer}
                  allowClear
                  className="analytics-filter-centered-search-select"
                />
              </Col>
              <Col span={6}>
                <Select
                  mode="multiple"
                  showSearch
                  placeholder="Default (All Categories)"
                  style={{ width: "100%" }}
                  options={categories || []}
                  onChange={handleCategoryChange}
                  optionFilterProp="label"
                  value={selectedCategories}
                  allowClear
                  className="analytics-filter-centered-search-select"
                />
              </Col>
              <Col span={6}>
                <Select
                  placeholder="Select Date"
                  style={{ width: "100%" }}
                  options={dateOptions}
                  onChange={handleDateChange}
                  onSelect={handleDateSelect}
                  value={selectedValue}
                  className="analytics-filter-centered-search-select"
                />
              </Col>
              {selectedValue === 'custom' && (
                <Col span={6} style={{ marginTop: 12 }}>
                  <DatePicker.RangePicker
                    value={customStartDate && customEndDate ? [customStartDate, customEndDate] : null}
                    onChange={handleCustomDateChange}
                    format="YYYY-MM-DD"
                    placeholder={["Start Date", "End Date"]}
                    disabledDate={handleDisabledDates}
                    style={{ width: "100%" }}
                  />
                </Col>
              )}
            </Row>
          )}
        </div>
      }
    >
      <Row>
        <div className="chart-container">
          {isLoading ? (
            <LoadingOutlined className="loading-center top-20" />
          ) : (
            <Table
              dataSource={modelDetails}
              columns={getVisibleColumns()}
              bordered
              scroll={{ x: true }}
              style={{ overflowX: "scroll" }}
              rowKey={(record) => `${record.id}-${record.model_type}`}
              summary={getTableSummary}
              pagination={{
                current: currentPage,
                pageSize: pageSize,
                total: modelDetails.length,
                showQuickJumper: false,
              }}
              onChange={handleTableChange}
            />
          )}
        </div>
      </Row>
    </Card>
  );
};

export default withRouter(ModelCostDetails);
